import React, { useEffect, useMemo, useState } from 'react';
import { ContainerType } from '../../OrderFulfillment/Components/OrderFulfillmentAddContainerTypes';
import EventPriceTable, {
  DefaultContainerPriceMap,
  EventPriceType,
  StringifiedEventPrice,
} from '../../components/EventPricesTable';
import {
  EventPriceTarget,
  isSameTarget,
  priceTarget,
} from '../../components/eventPriceTarget';
import { useAllItemTypesQuery } from '../../../../graphql/queries/allItemTypes.generated';
import {
  EventPricesDocument,
  useEventPricesQuery,
} from '../../../../graphql/queries/eventPrices.generated';
import { isNullOrWhitespace } from '@/utils/stringHelper';
import { useUpdateEventAccountPricesMutation } from '../../../../graphql/mutations/updateEventAccountPrices.generated';
import { LayoutStoreInstance } from '../../../../LayoutStore';
import i18n from '@/utils/i18n';
import { Communicator } from '../../../../tools/Communication';
import { StoreDefaultContainerPrice } from '../../../ContainerTypes/ContainerPriceTable';

export type EventAccountPricesTabProps = {
  eventAccountId: string;
  events: { id: string; name?: string | null; currency: string }[];
};

export const EventAccountPricesTab = ({
  eventAccountId,
  events,
}: EventAccountPricesTabProps) => {
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const { data: allItemTypes } = useAllItemTypesQuery({
    variables: { filter: { archived: false }, skip: 0, take: 500 },
  });
  const [allContainerTypes, setAllContainerTypes] = React.useState<
    ContainerType[]
  >([]);
  const [prices, setPrices] = React.useState<StringifiedEventPrice[]>([]);
  const [storeDefaultContainerPrices, setStoreDefaultContainerPrices] =
    useState<StoreDefaultContainerPrice[]>([]);

  const eventCurrency =
    events.find((e) => e.id === selectedEvent)?.currency || '';

  const defaultContainerPrices = useMemo<DefaultContainerPriceMap>(() => {
    const map: DefaultContainerPriceMap = {};
    if (!eventCurrency) return map;
    const currencyKey = eventCurrency.toUpperCase();
    for (const entry of storeDefaultContainerPrices) {
      const row = entry[currencyKey];
      if (!row) continue;
      map[entry.Id] = {
        rental_price: row.RentalPrice,
        cleaning_price: row.CleaningPrice,
        deposit_price: row.DepositPrice,
        lost_price: row.LostPrice,
        logistic_partner_cleaning_price: row.LogisticPartnerCleaningPrice,
      };
    }
    return map;
  }, [storeDefaultContainerPrices, eventCurrency]);

  const {
    data: eventPrices,
    loading: loadingEventPrices,
    error: errorEventPrices,
  } = useEventPricesQuery({
    skip: isNullOrWhitespace(selectedEvent),
    variables: { event_account_id: eventAccountId, event_id: selectedEvent },
  });

  const [updatePrices, { loading: updatingPrices }] =
    useUpdateEventAccountPricesMutation({
      onError: (error) => {
        LayoutStoreInstance.setError(
          i18n.t('EventAccount:failedToUpdatePrices') + error,
        );
      },
      refetchQueries: [EventPricesDocument],
    });

  useEffect(() => {
    Communicator.Get('api/3/containers/getAllContainerTypes', {})
      .then((response) => response.json())
      .then((data) => {
        setAllContainerTypes(data);
      });
    Communicator.Get('/Config/GetConfigValue', {
      key: 'DefaultContainerInformation',
    })
      .then((response) => response.json())
      .then((data: StoreDefaultContainerPrice[]) => {
        setStoreDefaultContainerPrices(data);
      });
  }, []);

  useEffect(() => {
    if (eventPrices?.eventPrices != null) {
      setPrices(eventPrices.eventPrices as any);
    }
  }, [eventPrices]);

  function setPrice(
    priceType: EventPriceType,
    target: EventPriceTarget,
    value: string,
  ): void {
    setPrices((prevPrices) => {
      const newPrices = prevPrices.map((price) => {
        if (isSameTarget(priceTarget(price), target)) {
          return {
            ...price,
            // Prices are whole cents, so a decimal point has no meaning here. Letting
            // one through cost a factor of 100: emptyPriceToNull's parseInt read 2.50 as 2.
            [priceType]: value.replace(/[^0-9]/g, ''),
            is_default: false,
          };
        }
        return price;
      });
      return newPrices;
    });
  }
  return (
    <div>
      <div className="input-group mb-3">
        <div className="input-group-prepend">
          <span className="input-group-text" id="basic-addon1">
            {i18n.t('EventAccount:event')}
          </span>
        </div>
        <select
          className="form-control"
          value={selectedEvent}
          onChange={({ target: { value } }) => setSelectedEvent(value)}
        >
          <option key="default" value="">
            {i18n.t('EventAccount:selectEvent')}
          </option>
          {events.map((event) => (
            <option key={event.id} value={event.id}>
              {event.name || i18n.t('Common:unknown')}
            </option>
          ))}
        </select>
      </div>
      <EventPriceTable
        allContainerTypes={allContainerTypes}
        allItemTypes={allItemTypes?.itemTypes?.items ?? []}
        currency={eventCurrency}
        prices={prices}
        setPrice={setPrice}
        error={errorEventPrices}
        loading={loadingEventPrices || updatingPrices}
        savePrices={() =>
          updatePrices({
            variables: {
              prices: prices.map((price) => ({
                container_type_id: price.container_type_id,
                item_type_id: price.item_type_id,
                rental_price: emptyPriceToNull(price.rental_price),
                cleaning_price: emptyPriceToNull(price.cleaning_price),
                deposit_price: emptyPriceToNull(price.deposit_price),
                lost_price: emptyPriceToNull(price.lost_price),
                logistic_partner_cleaning_price: emptyPriceToNull(
                  price.logistic_partner_cleaning_price,
                ),
              })),
              event_id: selectedEvent,
              event_account_id: eventAccountId,
            },
          })
        }
        showDefaultColumn
        defaultContainerPrices={defaultContainerPrices}
      />
    </div>
  );
};

function emptyPriceToNull(
  value: string | number | null | undefined,
): number | null {
  if (value == null || value === '') return null;
  const parsed = typeof value === 'number' ? value : parseInt(value);
  return Number.isNaN(parsed) ? null : parsed;
}
